import { z } from 'zod'
import type { IconName } from '../../../components/Icon.tsx'
import { useHailReportTypeRules, useLightningReportTypeRules } from '../../../data/analyze/reportTypeRules.ts'
import { useFlags } from '../../../launchdarkly.ts'
import type { ReportFormProps, ReportTypeConfig } from './CreateReportForm.tsx'
import { useHailReportType } from './hail/hailReportType.tsx'
import { useLightningReportType } from './lightning/lightningReportType.tsx'

export const ReportTypeSchema = z.enum(['lightning', 'hail'])
export type ReportType = z.infer<typeof ReportTypeSchema>

export type ReportTypeOption = { type: ReportType; icon: IconName }

export type ReportTypeSelection = {
	options: ReportTypeOption[]
	selected: ReportType | undefined
	onSelect: (reportType: ReportType) => void
}

export type ReportTypeEntry = ReportTypeOption & { config: ReportTypeConfig }

export const useReportTypes = (props: ReportFormProps): ReportTypeEntry[] => {
	const { analyzeHailReports } = useFlags()
	const lightning = useLightningReportType(props)
	const hail = useHailReportType(props)

	return [
		{ type: 'lightning', icon: 'LightningBolt', config: lightning },
		...(analyzeHailReports && hail ? [{ type: 'hail', icon: 'VHail', config: hail } as const] : []),
	]
}

export const useMaxAssetsOf = () => {
	const lightning = useLightningReportTypeRules()
	const hail = useHailReportTypeRules()
	return (type: ReportType | undefined) => (type === 'hail' && hail ? hail : lightning).maxAssets
}

export const useAllowUnprotectedAssets = (type: ReportType | undefined) => {
	const lightning = useLightningReportTypeRules()
	const hail = useHailReportTypeRules()
	// Hail rules are missing only while analyzeHailReports is off, so remove `&& hail` when the flag is removed
	return (type === 'hail' && hail ? hail : lightning).allowUnprotectedAssets
}
